import theme from '@themes/theme';

/**
 * Collapsed height of the New Task sheet before the keyboard is added.
 *
 * Web only now — iOS and Android take MOBILE_RATIOS below and open at
 * index 1, so nothing on a phone reads this.
 *
 * Raised from 400 for TA-10495 QA round 2, when the default content was
 * roughly handle (24) + Name (72) + Description at its 2-row default (96) +
 * the 16pt gap below it + the footer (136) + the home indicator (34) = ~378.
 *
 * That derivation is now historical on two counts, and neither is worth
 * re-deriving for a constant no phone reads: the attachments preview has since
 * moved out of the footer into the sheet's scroll content, so the footer is
 * the controls row alone; and the description caps at 3 rows rather than 5.
 * The stale 136 also survives as the Android `?? 136` fallback in
 * TaskCreateBottomSheet, which is likewise only a first-render placeholder
 * until the footer measures itself.
 */
export const NEW_TASK_MIN_HEIGHT = 480;

/**
 * Mobile popup height, copied from New Project's sheet
 * (`ProjectCreateBottomSheet`'s `initialSnapPoints`) so the two open the same
 * way. Emitted as percentages, which resolve against the sheet's own container
 * rather than a screen height this module has to be handed — that is also what
 * keeps the array sorted with the keyboard open, where the pixel collapsed
 * point below could overshoot the expanded one and come out DESCENDING.
 *
 * Callers open at index 1, as New Project does.
 */
const MOBILE_RATIOS: Record<string, number[]> = {
  // New Project's iOS pair is ['90%', '90%'] — the same point twice, so the
  // sheet has nothing shorter to fall back to. Index 0 is lowered here so it
  // can shrink once the keyboard is down and there is no longer a keyboard's
  // worth of screen to clear (TA-10495 QA round 3). Index 1, the point the
  // sheet opens at, is unchanged.
  ios: [0.75, 0.9],
  android: [0.7, 0.85],
};

/**
 * The screen the iOS ratios are read against. A percentage of the screen keeps
 * growing with the screen, so on a 6.9" Pro Max (932pt) 90% is ~84pt taller
 * than the same sheet on a 6.1" phone — the form does not have more to show
 * there, it just sits higher up (TA-10495 QA round 3).
 *
 * So each iOS ratio also carries an absolute ceiling: the height it reaches on
 * this reference device. Phones at or below it are unaffected — an iPhone 15
 * still resolves to exactly 90% — and larger ones stop growing instead.
 */
const REFERENCE_SCREEN_HEIGHT = 852;

/**
 * Android is deliberately NOT capped: it takes New Project's 70%/85% as they
 * are, on every screen.
 *
 * Android dp heights run well past this reference — a Pixel 7 is ~914dp — so
 * the cap fired on most devices and pulled 85% down to ~79%, which is what
 * made the sheet read as too short next to New Project's (TA-10495 QA round 3
 * follow-up). iOS keeps the cap: its complaint was the opposite one.
 */
const CAPPED_PLATFORMS = ['ios'];

/**
 * Due Date sheet content height, same philosophy as Activity's
 * `computeCustomDateHeight` (`filterSheetLayout.tsx`): measured from the
 * actual rendered device layout (header through the Reset/Apply row), not a
 * Figma frame or a screen ratio — a ratio left a large blank area below the
 * footer (TA-10614 QA round 8). `DatePicker` must be rendered with
 * `shouldHugContent` for this to hold: without it, its root box stretches to fill
 * whatever height it's given (`flex: 0.9`/`1`), which re-opens the same blank
 * space one layer in — inside the stretched box instead of below it
 * (TA-10614 QA round 12, matching the Activity sheet's own
 * `FilterSheetCustomDatePage` doc comment on why it avoids `flex: 1` too).
 *
 * `FilterSheetFooter` (the sheet's footer) appends its own
 * `insets.bottom` strip already, so that has to be added here too, on top of
 * the fixed 444 measured on a device with no bottom inset — not baked into
 * the constant, or a device with a home-indicator/gesture-nav inset would
 * overflow this box and reintroduce the blank-area bug one layer out.
 */
const DATE_MODAL_BASE_CONTENT_HEIGHT = 444;
export const computeDateModalContentHeight = (insetsBottom: number): number =>
  DATE_MODAL_BASE_CONTENT_HEIGHT + Math.max(insetsBottom, 0);

// The shared grabber handle above the content (`closeLine` in
// TaskCreateBottomSheet.tsx: a 4pt bar + `theme.spacing.l` margin on each
// side) — rendered by gorhom's own `handleComponent`, outside the content box
// `computeDateModalContentHeight` sizes, so it is added on top rather than
// folded into that budget. The margins are tied to `theme.spacing.l` so this
// stays in sync if the design-system token changes; `closeLine`'s 4pt bar
// height is a literal in its own StyleSheet entry, not itself a token.
export const DATE_MODAL_HANDLE_HEIGHT = 4 + theme.spacing.l * 2;
export const computeDateModalSnapHeight = (insetsBottom: number): number =>
  computeDateModalContentHeight(insetsBottom) + DATE_MODAL_HANDLE_HEIGHT;

/**
 * Height the Task Form sheet collapses to while the Due Date sheet is open —
 * a genuine snap point (swapped in as `initialSnapPoints`' index-0 VALUE
 * whenever the Date Modal is open), not a `snapToPosition` call to an
 * arbitrary value. @gorhom/bottom-sheet treats ANY `snapToPosition`/`dismiss`
 * landing outside the sheet's configured `snapPoints` as a real close
 * (`nextIndex === -1` unconditionally fires `handleOnClose` — see
 * `animateToPositionCompleted` in the library's own `BottomSheet.tsx`), which
 * is indistinguishable from the sheet actually leaving
 * `BottomSheetModalProvider`'s stack — breaking the Date Modal's own
 * `stackBehavior='push'`, which needs something already-presented to push
 * onto (TA-10614 QA round 8: this is exactly what made the Due Date sheet
 * open and immediately bounce back closed). Being a real, configured snap
 * point instead means `snapToIndex(0)` here is a completely ordinary snap —
 * same mechanism as any other collapse in this file, never -1.
 */
export const TASK_FORM_DATE_MODAL_COLLAPSE_HEIGHT = 40;

/** Ratio, reduced so the sheet never exceeds its height on the reference. */
const cappedRatio = (
  ratio: number,
  screenHeight: number,
  platform: string
): number =>
  CAPPED_PLATFORMS.includes(platform) && screenHeight > 0
    ? Math.min(ratio, (ratio * REFERENCE_SCREEN_HEIGHT) / screenHeight)
    : ratio;

/**
 * Fraction of the screen the expanded snap point(s) occupy.
 *
 * Web only, now that iOS and Android take MOBILE_RATIOS above — and this
 * sheet does not currently render on web at all (TaskFormStack gates it behind
 * `Platform.OS !== 'web'`), so this is the fallback path, kept intact.
 */
const EXPANDED_RATIOS: Record<string, number[]> = {
  ios: [0.8],
  android: [0.85],
  web: [0.7, 0.9],
};

type SnapPointInput = {
  screenHeight: number;
  keyboardHeight: number;
  platform: string;
};

/**
 * Snap points for the New Task bottom sheet.
 *
 * iOS and Android return New Project's percentage points verbatim. Everything
 * below describes the web fallback, which still resolves to pixels.
 *
 * @gorhom/bottom-sheet requires a sorted array. The collapsed point is a pixel
 * height (NEW_TASK_MIN_HEIGHT + keyboardHeight) while the expanded ones used to
 * be '%' strings, so with the keyboard open the collapsed point could overshoot
 * the expanded one — 400 + ~336 = 736 against '80%' of an iPhone 15's 852pt
 * (682) — and the array came out DESCENDING. The description-growth effect then
 * snapped the sheet SMALLER when it meant to expand it.
 *
 * Resolving every point to pixels lets the collapsed one be dropped outright
 * once it no longer fits below the expanded one; callers must therefore snap to
 * `points.length - 1` rather than a hard-coded index.
 */
export const resolveTaskSheetSnapPoints = ({
  screenHeight,
  keyboardHeight,
  platform,
}: SnapPointInput): (number | string)[] => {
  const mobileRatios = MOBILE_RATIOS[platform];
  if (mobileRatios) {
    return mobileRatios.map(
      (ratio) =>
        `${Math.round(cappedRatio(ratio, screenHeight, platform) * 100)}%`
    );
  }

  const ratios = EXPANDED_RATIOS[platform] ?? EXPANDED_RATIOS.web;
  const expandedPoints = ratios.map((ratio) =>
    Math.round(screenHeight * ratio)
  );
  const collapsedHeight = NEW_TASK_MIN_HEIGHT + Math.max(keyboardHeight, 0);

  return collapsedHeight < expandedPoints[0]
    ? [collapsedHeight, ...expandedPoints]
    : expandedPoints;
};

/**
 * Height of the New Task sheet above the keyboard during create-task
 * onboarding (TA-10920 #11), after the Tutorialization design: drag handle
 * (~24) + Name row (~65) + a single Description row with its padding (~40) +
 * the project / submit row (~56), tuned to 177 on device. The design mock omits
 * the handle, which is why reading 154 straight off it hid the Description
 * under the footer. The attachment / date / member row is hidden in this flow,
 * so the regular percentage points would leave a tall empty block instead.
 */
export const ONBOARDING_TASK_CONTENT_HEIGHT = 177;

/**
 * Bottom inset the New Task sheet's footer is padded up by, so it sits on top
 * of the keyboard (or the home indicator once the keyboard is down).
 *
 * Android adds the keyboard as well: the app runs edge-to-edge
 * (react-native-edge-to-edge), so the window does NOT resize for the keyboard
 * despite `android_keyboardInputMode='adjustResize'`, and the keyboard height
 * reported by `keyboardDidShow` excludes the navigation bar inset.
 *
 * Shared by the footer and the onboarding snap points below, which must agree:
 * when the onboarding sheet assumed Android resized the window and left the
 * keyboard out, its fixed height ended up shorter than the lifted footer and
 * the keyboard covered the sheet (TA-10920 #11, Jack's Android test).
 */
export const resolveTaskSheetFooterBottomInset = ({
  keyboardHeight,
  bottomInset,
  platform,
}: {
  keyboardHeight: number;
  bottomInset: number;
  platform: string;
}): number => {
  const inset = Math.max(bottomInset, 0);
  if (keyboardHeight <= 0) return inset;
  return platform === 'android' ? keyboardHeight + inset : keyboardHeight;
};

/**
 * Snap points for the onboarding New Task sheet: one content-fit height at
 * both indices, since callers open at index 1 and drop to index 0 when the
 * keyboard goes down. The sheet is NOT lifted above the keyboard on its own —
 * its footer is padded up instead (resolveTaskSheetFooterBottomInset) — so
 * that same inset is part of the sheet's height, on every platform.
 */
export const resolveOnboardingTaskSheetSnapPoints = ({
  keyboardHeight,
  bottomInset,
  platform,
  descriptionExtraHeight = 0,
}: {
  keyboardHeight: number;
  bottomInset: number;
  platform: string;
  /** Rows the description has grown past its 1-row default, in pt. */
  descriptionExtraHeight?: number;
}): number[] => {
  const bottom = resolveTaskSheetFooterBottomInset({
    keyboardHeight,
    bottomInset,
    platform,
  });
  // The sheet has a fixed height here, so it grows with the description —
  // otherwise the extra rows would slide under the project / submit footer.
  const height =
    ONBOARDING_TASK_CONTENT_HEIGHT +
    Math.max(descriptionExtraHeight, 0) +
    bottom;
  return [height, height];
};
